// Radha — Zoho Books sync reads + triggers, direct path (migration 051).
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { supabase } from "../lib/supabase";
import { edgeFunctionErrorMessage } from "../lib/edgeFunctionError";

export interface ErpSyncLog {
  id: string; provider: string; direction: "pull_invoices" | "push_payment";
  status: "running" | "completed" | "failed";
  records_processed: number; error_detail: string | null;
  created_at: string; completed_at: string | null;
}

/** What zoho-status reports. `state` is the single field the UI branches on. */
export type ZohoState =
  | "not_configured" | "auth_failed" | "api_failed" | "org_mismatch" | "connected";

export interface ZohoStatus {
  state: ZohoState;
  connected: boolean;
  secrets: Record<string, boolean>;
  missing: string[];
  accounts_domain: string;
  api_domain: string;
  org: { id: string; name: string; currency: string; country: string; time_zone: string } | null;
  error: string | null;
  hint?: string;
  visible_orgs?: Array<{ id: string; name: string }>;
}

export function useErpSyncLog(limit = 25) {
  return useQuery({
    queryKey: ["erp-sync-log", limit],
    queryFn: async (): Promise<ErpSyncLog[]> => {
      const { data, error } = await supabase
        .from("erp_sync_log")
        .select("id, provider, direction, status, records_processed, error_detail, created_at, completed_at")
        .order("created_at", { ascending: false })
        .limit(limit);
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 10_000,
  });
}

/**
 * Connection health. Deliberately a separate call from the sync: pressing Sync
 * to discover you have no credentials conflates a setup problem with a data
 * problem, and only one of them is urgent.
 */
export function useZohoStatus() {
  return useQuery({
    queryKey: ["zoho-status"],
    queryFn: async (): Promise<ZohoStatus> => {
      const { data, error } = await supabase.functions.invoke("zoho-status", { body: {} });
      if (error) throw new Error(await edgeFunctionErrorMessage(error));
      return data as ZohoStatus;
    },
    staleTime: 60_000,
    retry: false,
  });
}

/**
 * How much of the book actually came from Zoho. The number that says whether
 * the integration is load-bearing or decorative — a sync that "succeeds" while
 * linking nothing looks identical in a log.
 */
export function useZohoCoverage() {
  return useQuery({
    queryKey: ["zoho-coverage"],
    queryFn: async () => {
      const total = await supabase.from("invoices").select("id", { count: "exact", head: true });
      if (total.error) throw total.error;
      const linked = await supabase
        .from("invoices").select("id", { count: "exact", head: true })
        .not("zoho_invoice_id", "is", null);
      if (linked.error) throw linked.error;
      return { total: total.count ?? 0, linked: linked.count ?? 0 };
    },
    staleTime: 30_000,
  });
}

export function useSyncZohoInvoices() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke("zoho-sync-invoices", { body: {} });
      if (error) throw new Error(await edgeFunctionErrorMessage(error));
      if (data?.error) throw new Error(data.error);
      return data as { synced: number; total_seen: number };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["erp-sync-log"] });
      qc.invalidateQueries({ queryKey: ["zoho-coverage"] });
      qc.invalidateQueries({ queryKey: ["invoicing-direct"] });
    },
  });
}

export function usePushZohoPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (invoiceId: string) => {
      const { data, error } = await supabase.functions.invoke("zoho-push-payment", { body: { invoice_id: invoiceId } });
      if (error) throw new Error(await edgeFunctionErrorMessage(error));
      if (data?.error) throw new Error(data.error);
      return data as { zoho_payment_id: string | null; amount_pushed: number };
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["erp-sync-log"] }),
  });
}

export function zohoErrorMessage(err: unknown): string {
  const msg = err instanceof Error ? err.message : String(err);
  const m = msg.match(/^([A-Z_]+):\s*(.*)$/);
  return m ? m[2] || m[1] : msg;
}
